import {XIcon} from '@primer/octicons-react'
import {useCallback, useRef, useState} from 'react'

import styles from './TagInput.module.css'

type TagInputProps = {
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  variant?: 'allowed' | 'blocked'
  'aria-label': string
}

export function TagInput({values, onChange, placeholder = 'Add...', variant = 'allowed', ...rest}: TagInputProps) {
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const addTag = useCallback(
    (tag: string) => {
      const trimmed = tag.trim()
      if (trimmed && !values.includes(trimmed) && !(variant === 'blocked' && trimmed === '*')) {
        onChange([...values, trimmed])
      }
      setInputValue('')
    },
    [values, onChange, variant],
  )

  const removeTag = useCallback(
    (index: number) => {
      onChange(values.filter((_, i) => i !== index))
    },
    [values, onChange],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault()
        if (inputValue.trim()) {
          addTag(inputValue)
        }
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'Backspace' && inputValue === '' && values.length > 0) {
        removeTag(values.length - 1)
      }
    },
    [inputValue, values, addTag, removeTag],
  )

  const handleBlur = useCallback(() => {
    if (inputValue.trim()) {
      addTag(inputValue)
    }
  }, [inputValue, addTag])

  const tagClassName = variant === 'allowed' ? styles.tagAllowed : styles.tagBlocked

  return (
    <div className={styles.tagInputWrapper} role="group" aria-label={rest['aria-label']}>
      {values.map((tag, index) => (
        <span key={tag} className={`${styles.tag} ${tagClassName}`}>
          {tag}
          <button
            type="button"
            className={styles.tagRemoveButton}
            onClick={e => {
              e.stopPropagation()
              removeTag(index)
            }}
            aria-label={`Remove ${tag}`}
          >
            <XIcon size={12} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        className={styles.tagInput}
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={values.length === 0 ? placeholder : ''}
        aria-label={rest['aria-label']}
        data-testid="tag-input-field"
      />
    </div>
  )
}
